import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  PlayCircleOutline as PlayIcon,
  Book as BookIcon,
  Link as LinkIcon,
  Group as GroupIcon,
  Translate as TranslateIcon,
  FormatListBulleted as ListIcon,
  AccountTree as TreeIcon,
} from '@mui/icons-material';

interface LessonContentProps {
  onBack: () => void;
  onComplete: () => void;
  isCompleted: boolean;
}

const BusinessGlossaryLesson: React.FC<LessonContentProps> = ({
  onBack,
  onComplete,
  isCompleted,
}) => {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mr: 2 }}
        >
          Back to Lessons
        </Button>
        <Button
          variant={isCompleted ? "outlined" : "contained"}
          color={isCompleted ? "success" : "primary"}
          onClick={onComplete}
          startIcon={isCompleted ? <CheckCircleIcon /> : <PlayIcon />}
        >
          {isCompleted ? "Completed" : "Mark as Complete"}
        </Button>
      </Box>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Working with Business Glossary
        </Typography>
        
        <Typography variant="body1" paragraph>
          A Business Glossary is essential for establishing a common language across your 
          organization. Learn how to create, maintain, and effectively use a business glossary 
          to improve data understanding and collaboration.
        </Typography>

        <Alert severity="info" sx={{ my: 3 }}>
          A well-maintained Business Glossary helps eliminate confusion, ensures consistency, 
          and promotes better communication between business and technical teams.
        </Alert>

        <Typography variant="h5" sx={{ mt: 4, mb: 3 }}>
          Anatomy of a Business Term
        </Typography>

        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.light' }}>
                <TableCell sx={{ color: 'primary.contrastText' }}>Component</TableCell>
                <TableCell sx={{ color: 'primary.contrastText' }}>Description</TableCell>
                <TableCell sx={{ color: 'primary.contrastText' }}>Example</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Unique identifier for the term</TableCell>
                <TableCell>Customer Lifetime Value</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Definition</TableCell>
                <TableCell>Clear, concise explanation</TableCell>
                <TableCell>The predicted net profit from entire future relationship with a customer</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Current state in workflow</TableCell>
                <TableCell>Approved, Draft, Under Review</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Steward</TableCell>
                <TableCell>Person responsible for the term</TableCell>
                <TableCell>Data Governance Team</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h5" sx={{ mt: 4, mb: 3 }}>
          Key Features of a Business Glossary
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TreeIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Hierarchical Structure
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Organize terms in a logical hierarchy with parent-child relationships 
                  and categories for easy navigation.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LinkIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Term Relationships
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Define relationships between terms: synonyms, related terms, 
                  broader/narrower terms, and more.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <GroupIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Collaboration
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Enable comments, discussions, and workflows for term review 
                  and approval processes.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h5" sx={{ mt: 4, mb: 3 }}>
          Best Practices for Term Creation
        </Typography>

        <List>
          <ListItem>
            <ListItemIcon>
              <BookIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Be Clear and Concise" 
              secondary="Write definitions that are easy to understand and avoid jargon when possible"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <TranslateIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Use Standard Language" 
              secondary="Maintain consistency in terminology and formatting across all definitions"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ListIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Include Examples" 
              secondary="Provide real-world examples to illustrate term usage and context"
            />
          </ListItem>
        </List>

        <Typography variant="h5" sx={{ mt: 4, mb: 3 }}>
          Governance and Maintenance
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', backgroundColor: 'success.light' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="success.contrastText">
                  Regular Activities
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: 'success.contrastText' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Review terms periodically"
                      secondary="Ensure definitions remain accurate and relevant"
                      sx={{ 
                        '& .MuiListItemText-primary': { color: 'success.contrastText' },
                        '& .MuiListItemText-secondary': { color: 'success.contrastText' }
                      }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: 'success.contrastText' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Update relationships"
                      secondary="Maintain term connections and hierarchies"
                      sx={{ 
                        '& .MuiListItemText-primary': { color: 'success.contrastText' },
                        '& .MuiListItemText-secondary': { color: 'success.contrastText' }
                      }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', backgroundColor: 'error.light' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="error.contrastText">
                  Common Pitfalls
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: 'error.contrastText' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Neglecting updates"
                      secondary="Outdated terms lead to confusion"
                      sx={{ 
                        '& .MuiListItemText-primary': { color: 'error.contrastText' },
                        '& .MuiListItemText-secondary': { color: 'error.contrastText' }
                      }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: 'error.contrastText' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Inconsistent formatting"
                      secondary="Reduces glossary effectiveness"
                      sx={{ 
                        '& .MuiListItemText-primary': { color: 'error.contrastText' },
                        '& .MuiListItemText-secondary': { color: 'error.contrastText' }
                      }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" gutterBottom>
          Practical Tips for Success
        </Typography>

        <List>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="success" />
            </ListItemIcon>
            <ListItemText 
              primary="Start Small and Scale"
              secondary="Begin with key terms in one business area before expanding"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="success" />
            </ListItemIcon>
            <ListItemText 
              primary="Engage Stakeholders"
              secondary="Involve business users in term definition and review"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="success" />
            </ListItemIcon>
            <ListItemText 
              primary="Measure Usage"
              secondary="Track term usage and user engagement to identify areas for improvement"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="success" />
            </ListItemIcon>
            <ListItemText 
              primary="Promote Adoption"
              secondary="Make the glossary easily accessible and integrate it into daily workflows"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Platform Comparison: Unidatum vs E-Unify
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Understanding the differences between data intelligence platforms helps you choose 
          the right tools for your organization's needs. Both platforms serve distinct but 
          complementary purposes in the data ecosystem.
        </Alert>

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          Core Purpose & Mission
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #003366' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366' }}>
                  Unidatum.com
                </Typography>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Data Intelligence Platform
                </Typography>
                <Typography variant="body2" paragraph>
                  Centralized data intelligence, governance, and quality management across 
                  enterprise systems. Focuses on data trust, compliance, and AI governance.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Primary Users:
                </Typography>
                <Typography variant="body2">
                  Data governance teams, data stewards, AI model managers, and compliance officers
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #B31B1B' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: '#B31B1B' }}>
                  E-Unify.ai
                </Typography>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  AI Workflow & Integration Platform
                </Typography>
                <Typography variant="body2" paragraph>
                  AI automation, orchestration, and cross-application intelligent agent deployment. 
                  Emphasizes workflow optimization and system integration.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Primary Users:
                </Typography>
                <Typography variant="body2">
                  Business operations, developers, AI engineers, and integration specialists
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Key Capabilities Comparison
        </Typography>

        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#003366' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Feature Category</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unidatum.com</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>E-Unify.ai</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Key Capabilities</TableCell>
                <TableCell>
                  Data catalog, AI governance, observability, automated data quality validation, 
                  lifecycle tracking for data products
                </TableCell>
                <TableCell>
                  AI workflow builder, intelligent agents, cross-app AI orchestration 
                  (MCP Orchestration, AI Toolsets, Knowledge Bases)
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>AI Functionality</TableCell>
                <TableCell>
                  Unidatum AI Copilot for natural-language data search and task automation; 
                  AI model tracking for compliance and bias monitoring
                </TableCell>
                <TableCell>
                  Embedded AI agents for process automation, multi-app orchestration, 
                  and knowledge automation
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Governance Focus</TableCell>
                <TableCell>
                  Deep governance and lineage features across data, metadata, and AI assets 
                  to ensure policy compliance and transparency
                </TableCell>
                <TableCell>
                  Workflow governance through automation rules, not formal data governance frameworks
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Data Management</TableCell>
                <TableCell>
                  Unified data catalog, quality monitoring, lineage visualization, 
                  and product lifecycle tracker
                </TableCell>
                <TableCell>
                  AI-driven workflow orchestration for structured and unstructured data flows
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Integration</TableCell>
                <TableCell>
                  Integrates via Unidatum Cloud Sites and APIs with AWS, Google, Snowflake, and Slack
                </TableCell>
                <TableCell>
                  Supports API-based integration across apps, enabling AI to act in multiple 
                  systems simultaneously
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Enterprise Use Cases</TableCell>
                <TableCell>
                  Data discovery, AI model tracking, regulatory compliance, metadata management, 
                  and trusted data products
                </TableCell>
                <TableCell>
                  Intelligent automation, business process optimization, and unified AI interaction 
                  across tools like CRMs and project platforms
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Strategic Insights
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', backgroundColor: '#e3f2fd' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  When to Choose Unidatum
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Regulated enterprises needing traceability"
                      secondary="Financial services, healthcare, government agencies"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Policy enforcement and compliance"
                      secondary="GDPR, HIPAA, SOX, and other regulatory requirements"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Data strategy and analytics maturity"
                      secondary="Building a foundation for trusted data products"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', backgroundColor: '#fce4ec' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: '#B31B1B' }}>
                  When to Choose E-Unify
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#B31B1B' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Dynamic system integration needs"
                      secondary="Connecting disparate applications and data silos"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#B31B1B' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="AI-driven process automation"
                      secondary="Workflow optimization and productivity enhancement"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#B31B1B' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Lightweight, automation-first approach"
                      secondary="Quick deployment without heavy governance structure"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Complementary Approach
          </Typography>
          <Typography variant="body2">
            These platforms can work together: <strong>Unidatum</strong> ensures trustworthy 
            data foundations through governance and quality management, while <strong>E-Unify.ai</strong> 
            operationalizes that data through AI-driven automation and interaction layers. 
            Together, they create a comprehensive data and AI ecosystem.
          </Typography>
        </Alert>

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          E-Unify Business Glossary Features
        </Typography>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  40+ Data Fields
                </Typography>
                <Typography variant="body2">
                  Comprehensive term attributes including name, definition, synonyms, category, 
                  domain, ownership, and custom attributes
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Approval Workflow
                </Typography>
                <Typography variant="body2">
                  Complete lifecycle management with draft, review, approval, and deprecation 
                  states with full audit trail
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  508 Compliance
                </Typography>
                <Typography variant="body2">
                  Full accessibility support with keyboard navigation, screen reader compatibility, 
                  and WCAG 2.0 AA standards
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Call to Action for Comprehensive Training */}
        <Alert 
          severity="info" 
          sx={{ 
            mt: 4, 
            backgroundColor: '#e3f2fd',
            border: '2px solid #003366',
            '& .MuiAlert-icon': {
              color: '#003366'
            }
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#003366' }}>
            🎓 Ready for Advanced Training?
          </Typography>
          <Typography variant="body1" paragraph>
            Take your Business Glossary skills to the next level with our comprehensive training module. 
            Complete all lessons and earn a professional certificate of completion!
          </Typography>
          <Button
            variant="contained"
            color="primary"
            href="/business-glossary/training"
            sx={{
              mt: 2,
              bgcolor: '#003366',
              '&:hover': { bgcolor: '#002244' },
              fontWeight: 600
            }}
          >
            Start Comprehensive Training →
          </Button>
        </Alert>
      </Paper>
    </Box>
  );
};

export default BusinessGlossaryLesson;
